import { formatPetDisplayValue } from '../dicom/intensityUnits';
import { Modality } from '../dicom/modality';
import type { Image3D } from './image3d';
import type { SuvQuantification } from './petSuv';

/** Whether an image is a PET volume, whose values are read in SUV or activity units. */
export function isPetImage(image: Image3D | undefined): image is Image3D {
    return image?.firstSlice.modality === Modality.PT;
}

/**
 * The PET image of a drawn pair, or undefined when neither side is PET.
 * The secondary wins when both are: it is the side the renderer draws
 * through the PET colour ramp as the overlay.
 */
export function petImageOfPair(
    primary: Image3D | undefined,
    secondary: Image3D | undefined
): Image3D | undefined {
    if (isPetImage(secondary)) {
        return secondary;
    }
    return isPetImage(primary) ? primary : undefined;
}

/**
 * Whether the image's values read as SUVbw, which is what lets a readout
 * say "SUVmax" rather than "maximum uptake".
 */
export function hasSuvScale(image: Image3D): boolean {
    return image.suvFactor !== null;
}

/**
 * The image's highest value in its display unit, e.g. "12.4 SUVbw [g/ml]"
 * or "45.3 kBq/ml". Null while the image has no measured maximum: a volume
 * that is still loading, or whose load ended short, reports a placeholder
 * there, which is never printed as a value.
 */
export function formatPetMaxIntensity(image: Image3D): string | null {
    return image.hasMeasuredIntensityRange
        ? formatPetDisplayValue(image.maxValue, image.suvQuantification)
        : null;
}

/**
 * A fraction of the image's highest value in its display unit, for a
 * control that positions itself on the 0..1 span of that range. Null while
 * the image has no measured maximum to take the fraction of.
 */
export function formatNormalizedPetIntensity(
    image: Image3D,
    normalized: number
): string | null {
    return image.hasMeasuredIntensityRange
        ? formatPetDisplayValue(
              normalized * image.maxValue,
              image.suvQuantification
          )
        : null;
}

/**
 * Why a PET's values are not read as SUVbw, and what they are read as
 * instead, for the one-time warning a non-quantified PET raises. The
 * reasons and the fallback come from the quantification record, so a GML
 * or counts series is never described as Bq/ml.
 */
export function describeSuvUnavailability(suv: SuvQuantification): string {
    const reasons =
        suv.missingParams.length > 0
            ? suv.missingParams.join(', ')
            : 'PET metadata';
    const fallback =
        suv.units === 'BQML'
            ? 'Displaying raw intensity (Bq/ml).'
            : suv.state === 'counts'
              ? 'Displaying raw counts.'
              : suv.state === 'preconverted-other'
                ? `Displaying scanner values (SUV ${suv.suvType ?? 'BW'}, g/ml).`
                : 'Displaying raw stored values.';
    return `SUVbw values cannot be computed. ${reasons}. ${fallback}`;
}
